import { cn } from '../utils/cn'

/**
 * Circular in-flight indicator sized to drop straight into an icon's
 * slot — the icon is swapped for this while the request is in the air,
 * so the row doesn't jump.
 *
 * Exists because a raw icon `<button>` can't use `<Button loading>`:
 * disabling it fades the icon by half and nothing about that reads as
 * "received, working". An operator who gets no answer clicks again.
 *
 * `size` is in px and matches the icon it replaces (lucide's `size`
 * prop takes the same number). It rides an inline style rather than a
 * Tailwind `h-*`/`w-*` class because Tailwind can't generate a class
 * from a runtime value. Colour is inherited — put the tint on the
 * button, not here.
 *
 * No icon-library dependency on purpose: this ships from the shared
 * package and every consumer already renders it identically.
 */
export function InlineSpinner({
  size = 14,
  className,
}: {
  readonly size?: number
  readonly className?: string
}) {
  return (
    <svg
      className={cn('shrink-0 animate-spin', className)}
      style={{ width: size, height: size }}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
    </svg>
  )
}
